import { Helmet } from "react-helmet"
import api from "../services/api"
import { useEffect, useMemo, useState } from "react"
import FloorMapSkeleton from "./skeleton/FloorMapSkeleton"

// 1) Define clickable/highlight areas as % of the IMAGE container
// NOTE: You must tweak these values to match your exact floor map image.
const ROOM_AREAS = [
  // TOP ROW
  { roomNumber: "C101", top: 8.5, left: 16.0, width: 29, height: 24 },
  { roomNumber: "C102", top: 8.5, left: 55.0, width: 31, height: 24 },

  // MIDDLE ROW
  { roomNumber: "B201", top: 34.5, left: 16.0, width: 29, height: 23 },
  { roomNumber: "B202", top: 35.5, left: 55.2, width: 31, height: 22 },

  // BOTTOM ROW
  { roomNumber: "A101", top: 59.5, left: 16.0, width: 29, height: 23 },
  { roomNumber: "A102", top: 60.0, left: 55.0, width: 31, height: 22.4 },
]

export default function FloorMap() {
  const [units, setUnits] = useState([])
  const [rooms, setRooms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true)
        setError("")

        const [unitsRes, roomsRes] = await Promise.all([
          api.get(`/api/units`),
          api.get(`/api/rooms`),
        ])
        setUnits(unitsRes.data || [])
        setRooms(roomsRes.data || [])
      } catch (e) {
        setError("Failed to load floor map availability.")
      } finally {
        setLoading(false)
      }
    }

    run()
  }, [])

  // Unit numbers already assigned (occupied)
  const assignedRoomNumbers = useMemo(() => {
    return new Set((units || []).map((u) => u.unitNumber).filter(Boolean))
  }, [units])

  // Quick lookup: roomNumber => { status, roomData }
  const roomStatus = useMemo(() => {
    const map = new Map()
    for (const r of rooms || []) {
      const num = r.roomNumber
      const isOccupied = assignedRoomNumbers.has(num)
      map.set(num, {
        status: isOccupied ? "Occupied" : "Vacant",
        room: r,
      })
    }
    return map
  }, [rooms, assignedRoomNumbers])

  if (loading) {
    return <FloorMapSkeleton />
  }
  return (
    <>
      <Helmet>
        <title>Floor Map - Stay Suite | Apartment Management System</title>
      </Helmet>

      <div className="p-6 w-full max-w-300 mx-auto">
        <h1 className="text-2xl font-bold mb-2">Floor Map</h1>
        <p className="pb-5 text-gray-600 italic text-balance text-sm">
          Monitor room availability and occupancy status through the floor
          layout below.
        </p>

        {/* Legend */}
        <div className="flex items-center gap-4 text-sm mb-4">
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded bg-green-500" />
            <span>Vacant (Available)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded bg-orange-400" />
            <span>Occupied</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded bg-gray-400" />
            <span>Empty</span>
          </div>
          {loading && <span className="text-gray-500">Loading…</span>}
          {error && <span className="text-red-600">{error}</span>}
        </div>

        {/* Image + Overlay */}
        <div className="w-full max-w-7xl md:max-w-218.75 m-auto">
          <div className="w-full overflow-x-auto md:overflow-visible">
            <div className="relative min-w-[520px] md:min-w-0 md:w-full">
              <img
                className="w-full select-none"
                src="/images/assets/1BR_ROOM_NO_TEXT.webp"
                alt="Floor Map"
                draggable={false}
              />
              {/* Overlay hotspots */}
              {ROOM_AREAS.map((area) => {
                const info = roomStatus.get(area.roomNumber)
                // If room doesn’t exist in DB, treat as unknown/disabled
                const status = info?.status || "Unknown"
                const isVacant = status === "Vacant"
                const isOccupied = status === "Occupied"

                return (
                  <button
                    key={area.roomNumber}
                    type="button"
                    title={`${area.roomNumber} • ${status}`}
                    className={[
                      "absolute rounded-md transition",
                      "focus:outline-none focus:ring-2 focus:ring-orange-400",
                      isVacant
                        ? "border-2 border-green-500 bg-green-500/20 animate-pulse"
                        : isOccupied
                          ? "border-2 border-orange-400 bg-orange-500/10"
                          : "border-2 border-gray-400 bg-gray-400/10",
                    ].join(" ")}
                    style={{
                      top: `${area.top}%`,
                      left: `${area.left}%`,
                      width: `${area.width}%`,
                      height: `${area.height}%`,
                    }}
                    onClick={() => {
                      // Optional: do something on click (toast, modal, navigate, etc.)
                      // Example:
                      // console.log("Clicked:", area.roomNumber, status)
                    }}
                  >
                    {/* small label inside the hotspot */}
                    <span className="sr-only">
                      {area.roomNumber} {status}
                    </span>

                    <span
                      className={[
                        "absolute -top-3 left-1/2 -translate-x-1/2",
                        "px-2 py-0.5 rounded text-[11px] font-semibold shadow",
                        isVacant
                          ? "bg-green-600 text-white"
                          : isOccupied
                            ? "bg-gray-600 text-white"
                            : "bg-yellow-500 text-black",
                      ].join(" ")}
                    >
                      {area.roomNumber}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
        <div className="w-full max-w-7xl md:max-w-218.75 m-auto">
          <p className="mt-2 text-xs text-gray-500 text-center">
            Disclaimer: This layout is provided for viewing purposes only and is
            not an official floor plan.
          </p>
        </div>
      </div>
    </>
  )
}
